import {
  Add as AddIcon,
  DeleteOutlineOutlined as DeleteIcon,
  FileUpload as ImportIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { useCurrency } from "../context/CurrencyContext.tsx";
import { useTheme } from "../context/ThemeContext.tsx";
import { canWrite } from "../shared/AccessList.ts";
import { Stage } from "../shared/CaseConfiguration.ts";
import { ClaimTypes } from "../shared/CaseModel.ts";
import { CaseListElement } from "../shared/CasesList.ts";
import { relativeTime } from "../utils/relativeTime.ts";
import { getColorForCosts } from "./analytics/CostColors.ts";
import DeleteCaseDialog from "./DeleteCaseDialog.tsx";
import { FUNDING_TYPES, FundingType, NO_FUNDING } from "./home/homeSummary.ts";
import { ImportCaseDialog } from "./ImportCaseDialog.tsx";
import { getClaimTypeChipStyle } from "./issues/claimTypeColors.ts";
import { getStageColor } from "./stageColors.ts";
import type { JudgmentStatus } from "../shared/judgment";

/* ----------------------------------------------------------------------- */
/* Display tokens                                                          */
/* ----------------------------------------------------------------------- */

/** Maps a funding/agreement type to a cost colour, matching the case cards. */
const FUNDING_COLOR: Record<string, Parameters<typeof getColorForCosts>[0]> = {
  lawFirmCfa: "lawFirmFee",
  barristerCfa: "barristerFee",
  thirdPartyFunding: "courtFee",
  afterTheEventInsurance: "expertFee",
};

type SettlementFilter = "All" | "None" | "Partial" | "Settled";
const SETTLEMENT_FILTERS: SettlementFilter[] = [
  "All",
  "None",
  "Partial",
  "Settled",
];

/** The cases with one kind of funding, with none of them, or all cases. */
type FundingFilter = "All" | FundingType | typeof NO_FUNDING;
const FUNDING_FILTERS: FundingFilter[] = ["All", ...FUNDING_TYPES, NO_FUNDING];

const DEFAULT_PAGE_SIZE = 50;

/* ----------------------------------------------------------------------- */
/* Helpers                                                                 */
/* ----------------------------------------------------------------------- */

type SortKey =
  "title" | "claimTypes" | "funding" | "stage" | "value" | "updatedAt";
type SortDir = "asc" | "desc";

/**
 * View model derived from a {@link CaseListElement}, with the labels and
 * sort keys the table needs precomputed once (sorting is locale-correct
 * because it runs on the rendered labels).
 */
interface Row {
  id: string;
  title: string;
  partiesLabel: string;
  claimTypes: string[];
  funding: string[];
  stage: Stage;
  value: number;
  updatedAt?: string;
  settlementStatus: "none" | "partial" | "full";
  judgmentStatus: JudgmentStatus;
  // the user may read the case and not change or delete it
  readOnly: boolean;
  claimTypesSort: string;
  fundingSort: string;
  searchText: string;
}

function compare(a: Row, b: Row, key: SortKey): number {
  switch (key) {
    case "title":
      return a.title.localeCompare(b.title);
    case "claimTypes":
      return a.claimTypesSort.localeCompare(b.claimTypesSort);
    case "funding":
      return a.fundingSort.localeCompare(b.fundingSort);
    case "stage":
      return a.stage - b.stage;
    case "value":
      return a.value - b.value;
    case "updatedAt":
      return (
        (a.updatedAt ? new Date(a.updatedAt).getTime() : 0) -
        (b.updatedAt ? new Date(b.updatedAt).getTime() : 0)
      );
  }
}

function matchesFunding(funding: string[], f: FundingFilter) {
  if (f === "All") return true;
  if (f === NO_FUNDING)
    return !FUNDING_TYPES.some((type) => funding.includes(type));
  return funding.includes(f);
}

function matchesSettlement(
  status: Row["settlementStatus"],
  f: SettlementFilter,
) {
  switch (f) {
    case "All":
      return true;
    case "None":
      return status === "none";
    case "Partial":
      return status === "partial";
    case "Settled":
      return status === "full";
  }
}

/* ----------------------------------------------------------------------- */
/* Component                                                               */
/* ----------------------------------------------------------------------- */

const COLUMNS: { key: SortKey; align?: "left" | "right" }[] = [
  { key: "title" },
  { key: "claimTypes" },
  { key: "funding" },
  { key: "stage" },
  { key: "value", align: "right" },
  { key: "updatedAt" },
];

export default function CasesList({
  cases,
  accessNames = [],
  pageSize = DEFAULT_PAGE_SIZE,
}: {
  cases: CaseListElement[];
  // the names the access list of a case can know the user under: a case that
  // grants none of them write access cannot be deleted (see canWrite)
  accessNames?: string[];
  pageSize?: number;
}) {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t, i18n } = useTranslation(undefined, { keyPrefix: "CasesList" });
  const navigate = useNavigate();

  // A link to the list can name the filters it opens with: the start page
  // leads to the cases of one claim type or of one kind of funding.
  const [linked] = useSearchParams();
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<"All" | ClaimTypes>(() => {
    const type = linked.get("claimType") as ClaimTypes | null;
    return type && Object.values(ClaimTypes).includes(type) ? type : "All";
  });
  const [fundingFilter, setFundingFilter] = useState<FundingFilter>(() => {
    const funding = linked.get("funding") as FundingFilter | null;
    return funding && FUNDING_FILTERS.includes(funding) ? funding : "All";
  });
  const [settlementFilter, setSettlementFilter] = useState<SettlementFilter>(
    () => {
      const settlement = linked.get("settlement") as SettlementFilter | null;
      return settlement && SETTLEMENT_FILTERS.includes(settlement)
        ? settlement
        : "All";
    },
  );
  const [sortKey, setSortKey] = useState<SortKey>("updatedAt");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [page, setPage] = useState(1);
  const [removedCase, setRemovedCase] = useState<CaseListElement>();
  const [importOpen, setImportOpen] = useState(false);

  // use the resolved (post-fallback) language so the formatter matches the
  // actually-rendered locale — the detector may report e.g. "de" while the app
  // only ships "en" resources and falls back to it.
  const locale = i18n.resolvedLanguage ?? "en";
  const rtf = useMemo(
    () => new Intl.RelativeTimeFormat(locale, { numeric: "auto" }),
    [locale],
  );

  // only offer filter chips for claim types that actually occur in the cases
  // (kept in canonical enum order)
  const claimTypeFilters = useMemo<("All" | ClaimTypes)[]>(() => {
    // a type the list is opened with stays on offer though no case has it
    const present = new Set<string>(typeFilter === "All" ? [] : [typeFilter]);
    cases.forEach((c) =>
      Object.keys(c.claimTypeCounts).forEach((type) => present.add(type)),
    );
    return [
      "All",
      ...Object.values(ClaimTypes).filter((ct) => present.has(ct)),
    ];
  }, [cases, typeFilter]);

  // CaseListElement → row view model (labels + sort keys precomputed).
  const allRows = useMemo<Row[]>(
    () =>
      cases.map(
        ({
          id,
          title,
          currentStage,
          parties,
          clientParty,
          claimValue,
          claimTypeCounts,
          agreementTypes,
          accessList,
          settlementStatus,
          judgmentStatus,
          updatedAt,
        }) => {
          const claimTypes = Object.keys(claimTypeCounts);
          const funding = agreementTypes;
          const partiesLabel = parties
            .map((p) =>
              p === clientParty ? t("partyClient", { party: p }) : p,
            )
            .join(" · ");
          return {
            id,
            title,
            partiesLabel,
            claimTypes,
            funding,
            updatedAt,
            settlementStatus,
            judgmentStatus,
            stage: currentStage ?? Stage.PreAction,
            value: claimValue ?? 0,
            readOnly: !canWrite(accessList, accessNames),
            claimTypesSort: claimTypes
              .map((claimType) => t(`claimTypes.${claimType}`))
              .sort()
              .join("|"),
            fundingSort: funding
              .map((agreementType) => t(`agreementTypes.${agreementType}`))
              .sort()
              .join("|"),
            searchText: `${title} ${partiesLabel}`.toLowerCase(),
          };
        },
      ),
    [cases, accessNames, t],
  );

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const dir = sortDir === "asc" ? 1 : -1;
    return allRows
      .filter((r) => typeFilter === "All" || r.claimTypes.includes(typeFilter))
      .filter((r) => matchesFunding(r.funding, fundingFilter))
      .filter((r) => matchesSettlement(r.settlementStatus, settlementFilter))
      .filter((r) => !q || r.searchText.includes(q))
      .sort((a, b) => compare(a, b, sortKey) * dir);
  }, [
    allRows,
    query,
    typeFilter,
    fundingFilter,
    settlementFilter,
    sortKey,
    sortDir,
  ]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  // keep the page in range as filters/sort change the result set
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);
  const pageRows = filteredRows.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const headCellSx = {
    py: 1.25,
    fontSize: 11.5,
    fontWeight: 600,
    color: "text.secondary",
    letterSpacing: ".05em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    borderBottom: `1px solid ${theme.palette.divider}`,
  } as const;

  return (
    <Box
      sx={{
        mx: "auto",
        px: 3.5,
        py: 3.25,
        color: "text.primary",
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2.25,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            letterSpacing: "-.01em",
          }}
        >
          {t("title")}
        </Typography>
        <Stack direction="row" spacing={1.25}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ImportIcon />}
            onClick={() => setImportOpen(true)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {t("import")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate("/addCase")}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {t("newCase")}
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: "hidden" }}>
        {/* Toolbar */}
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            p: 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <TextField
            size="small"
            placeholder={t("searchPlaceholder")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{
              flex: 1,
              minWidth: 220,
              maxWidth: 360,
              "& .MuiOutlinedInput-root": {
                bgcolor: "background.default",
                borderRadius: 1.75,
              },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ fontSize: 18, color: "text.secondary" }}
                    />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Stack
            direction="row"
            sx={{
              gap: 0.9,
              flexWrap: "wrap",
            }}
          >
            {claimTypeFilters.map((ct) => {
              const on = typeFilter === ct;
              const isAll = ct === "All";
              // selected chip carries its claim-type colour; unselected stays
              // neutral (claim-type colours like Defamation's are too light to
              // read as text/outline on white). "All" uses the primary colour.
              const style = isAll
                ? undefined
                : getClaimTypeChipStyle(ct, theme);
              const accent =
                style?.backgroundColor ?? theme.palette.primary.main;
              const onText = style?.color ?? theme.palette.primary.contrastText;
              return (
                <Chip
                  key={ct}
                  label={
                    isAll ? t("claimTypeFilters.all") : t(`claimTypes.${ct}`)
                  }
                  onClick={() => setTypeFilter(ct)}
                  sx={{
                    height: 30,
                    fontWeight: 600,
                    fontSize: 12.5,
                    borderRadius: 5,
                    bgcolor: on ? accent : "background.paper",
                    color: on ? onText : "text.secondary",
                    border: `1px solid ${on ? accent : theme.palette.divider}`,
                    "&:hover": {
                      bgcolor: on ? accent : "action.hover",
                    },
                  }}
                />
              );
            })}
          </Stack>
          <Box
            sx={{
              flex: 1,
            }}
          />
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              gap: 1,
              color: "text.secondary",
              fontSize: 13,
            }}
          >
            {t("funding.label")}
            <Select
              size="small"
              value={fundingFilter}
              onChange={(e) =>
                setFundingFilter(e.target.value as FundingFilter)
              }
              sx={{ fontSize: 13, "& .MuiSelect-select": { py: 0.75 } }}
            >
              {FUNDING_FILTERS.map((f) => (
                <MenuItem key={f} value={f}>
                  {f === "All" || f === NO_FUNDING
                    ? t(`funding.filters.${f}`)
                    : t(`agreementTypes.${f}`)}
                </MenuItem>
              ))}
            </Select>
            {t("settlement.label")}
            <Select
              size="small"
              value={settlementFilter}
              onChange={(e) =>
                setSettlementFilter(e.target.value as SettlementFilter)
              }
              sx={{ fontSize: 13, "& .MuiSelect-select": { py: 0.75 } }}
            >
              {SETTLEMENT_FILTERS.map((f) => (
                <MenuItem key={f} value={f}>
                  {t(`settlement.filters.${f}`)}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        </Stack>

        {/* Count */}
        <Box
          sx={{
            px: 2,
            py: 1.1,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            sx={{
              fontSize: 13,
              color: "text.secondary",
            }}
          >
            <Trans
              t={t}
              i18nKey="count"
              values={{ shown: filteredRows.length, total: cases.length }}
              components={{
                bold: (
                  <Box
                    component="span"
                    sx={{ color: "text.primary", fontWeight: 700 }}
                  />
                ),
              }}
            />
          </Typography>
        </Box>

        {/* Table */}
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "background.default" }}>
              {COLUMNS.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align ?? "left"}
                  sortDirection={sortKey === col.key ? sortDir : false}
                  sx={headCellSx}
                >
                  <TableSortLabel
                    active={sortKey === col.key}
                    direction={sortKey === col.key ? sortDir : "asc"}
                    onClick={() => handleSort(col.key)}
                  >
                    {t(`columns.${col.key}`)}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell sx={{ ...headCellSx, width: 44 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.map((c) => (
              <TableRow
                key={c.id}
                hover
                onClick={() => navigate(`/cases/${c.id}`)}
                sx={{
                  cursor: "pointer",
                  "& td": {
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  },
                }}
              >
                <TableCell>
                  <Typography
                    sx={{
                      fontSize: 14.5,
                      fontWeight: 600,
                      color: "text.primary",
                    }}
                  >
                    {c.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      color: "text.secondary",
                    }}
                  >
                    {c.partiesLabel}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Stack
                    direction="row"
                    sx={{
                      gap: 0.6,
                      flexWrap: "wrap",
                    }}
                  >
                    {c.claimTypes.map((type) => {
                      const { backgroundColor, color } = getClaimTypeChipStyle(
                        type,
                        theme,
                      );
                      return (
                        <Box
                          key={type}
                          component="span"
                          sx={{
                            display: "inline-block",
                            px: 1.1,
                            py: 0.4,
                            borderRadius: 1.25,
                            fontSize: 11.5,
                            fontWeight: 600,
                            bgcolor: backgroundColor,
                            color,
                          }}
                        >
                          {t(`claimTypes.${type}`)}
                        </Box>
                      );
                    })}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack
                    direction="row"
                    sx={{
                      gap: 0.6,
                      flexWrap: "wrap",
                    }}
                  >
                    {c.funding.map((f) => {
                      const costType = FUNDING_COLOR[f];
                      const borderColor = costType
                        ? getColorForCosts(costType, theme).borderColor
                        : theme.palette.divider;
                      return (
                        <Box
                          key={f}
                          component="span"
                          sx={{
                            px: 1,
                            py: 0.25,
                            border: `1px solid ${borderColor}`,
                            borderRadius: 1.25,
                            fontSize: 11,
                            fontWeight: 600,
                            color: "text.secondary",
                          }}
                        >
                          {t(`agreementTypes.${f}`)}
                        </Box>
                      );
                    })}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack
                    direction="row"
                    sx={{
                      alignItems: "center",
                      gap: 0.85,
                      fontSize: 13,
                      fontWeight: 500,
                      color: "text.primary",
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        flex: "none",
                        bgcolor: getStageColor(c.stage, theme),
                      }}
                    />
                    {t(`stages.${Stage[c.stage]}`)}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "text.primary",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {currency.formatCompact(c.value)}
                  </Typography>
                  {c.judgmentStatus !== "none" && (
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        mt: 0.5,
                        px: 0.9,
                        py: 0.25,
                        borderRadius: 1.25,
                        fontSize: 10.5,
                        fontWeight: 600,
                        bgcolor: theme.palette.action.selected,
                        color: "text.primary",
                      }}
                    >
                      {t(`judgment.${c.judgmentStatus}`)}
                    </Box>
                  )}
                  {c.judgmentStatus === "none" &&
                    c.settlementStatus !== "none" && (
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block",
                          mt: 0.5,
                          px: 0.9,
                          py: 0.25,
                          borderRadius: 1.25,
                          fontSize: 10.5,
                          fontWeight: 600,
                          bgcolor:
                            c.settlementStatus === "full"
                              ? theme.palette.data.positive.light
                              : theme.palette.warning.light,
                          color:
                            c.settlementStatus === "full"
                              ? theme.palette.data.positive.dark
                              : theme.palette.warning.dark,
                        }}
                      >
                        {t(`settlement.badge.${c.settlementStatus}`)}
                      </Box>
                    )}
                </TableCell>
                <TableCell>
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: "text.secondary",
                    }}
                  >
                    {c.updatedAt ? relativeTime(c.updatedAt, rtf) : ""}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Tooltip
                    title={c.readOnly ? t("deleteReadOnly") : t("delete")}
                  >
                    {/* a disabled button fires no events: the tooltip, and the
                        click that must not open the case, are the span's */}
                    <span onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        size="small"
                        // the tooltip names the span around the button
                        aria-label={t("delete")}
                        disabled={c.readOnly}
                        onClick={() =>
                          setRemovedCase(
                            cases.find(
                              (caseElement) => caseElement.id === c.id,
                            ),
                          )
                        }
                      >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Pagination */}
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            sx={{
              fontSize: 13,
              color: "text.secondary",
            }}
          >
            {t("pagination.page", { page, total: totalPages })}
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, p) => setPage(p)}
            size="small"
            shape="rounded"
          />
        </Stack>
      </Paper>

      {removedCase && (
        <DeleteCaseDialog
          caseId={removedCase.id}
          caseTitle={removedCase.title}
          deleteAction={`/cases/${removedCase.id}/delete`}
          onClose={() => setRemovedCase(undefined)}
          open
        />
      )}
      <ImportCaseDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
      />
    </Box>
  );
}
